<bic-popup (close)="close()" [customClass]="'w-[640px]'" [hasBackdrop]="true"
           [title]="isEdit ? 'Zeitplan bearbeiten' : 'Zeitplan anlegen'">
  <div bicPopupContent class="flex flex-col gap-4">
    <!-- Prüflauf -->
    @if (fixedDefinitionName !== null) {
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-gray-900">Prüflauf</span>
        <span class="text-sm text-gray-900 truncate">{{ fixedDefinitionName }}</span>
      </div>
    } @else {
      <bic-select (valueChange)="setDefinition($event)"
                  [label]="'Prüflauf'"
                  [placeholder]="'Prüflauf auswählen'"
                  [value]="form.definitionId ?? ''">
        @for (option of checkOptions; track option.id) {
          <bic-dropdown-element [value]="option.id">
            {{ option.name }}@if (option.startSelectionStep != null) {<span class="text-gray-400"> · Auswahl beim Start</span>}
          </bic-dropdown-element>
        }
      </bic-select>
      @if (checkOptions.length === 0) {
        <span class="text-xs text-gray-500">
          Es gibt keinen aktiven Prüflauf ohne Zeitplan in diesem Projekt.
        </span>
      }
    }

    <!-- Wiederholung -->
    <div class="flex flex-col gap-1">
      <div class="flex items-center justify-between gap-4">
        <span class="text-sm font-medium text-gray-900">Wiederholung</span>
        <label class="flex items-center gap-2 text-sm text-gray-500">
          Expertenmodus (Cron)
          <bic-toggle (ngModelChange)="setExpertMode($event)" [ngModel]="expertMode"></bic-toggle>
        </label>
      </div>
      @if (!expertMode) {
        <bic-button-group (valueChange)="setPattern($event)" [value]="form.pattern" class="w-fit">
          @for (option of patternOptions; track option.value) {
            <bic-button-group-item [value]="option.value">
              <span class="px-1 text-sm">{{ option.label }}</span>
            </bic-button-group-item>
          }
        </bic-button-group>
      }
    </div>

    @if (expertMode) {
      <div class="flex flex-col gap-1">
        <bic-input (valueChange)="setCron($event)"
                   [label]="'Cron-Ausdruck'"
                   [placeholder]="'*/15 7-18 * * 1-5'"
                   [showX]="false"
                   [value]="form.cronExpression">
        </bic-input>
        <span class="text-xs text-gray-500">
          5 Felder: Minute Stunde Tag Monat Wochentag, deutsche Zeit. Beispiel „*/15 7-18 * * 1-5“: werktags
          von 7 bis 18 Uhr alle 15 Minuten. Sind Tag und Wochentag gesetzt, müssen beide passen. Höchstens alle
          {{ minCronInterval }} Minuten.
        </span>
      </div>
    }

    @if (form.pattern === 'WEEKLY') {
      <div class="flex flex-row gap-4">
        <div class="flex flex-1 flex-col gap-1 min-w-0">
          <bic-select (valueChange)="setWeekdays($event)"
                      [label]="'Wochentage'"
                      [multiple]="true"
                      [placeholder]="'Wochentage auswählen'"
                      [value]="form.weekdays">
            @for (day of weekdayOptions; track day.value) {
              <bic-dropdown-element [selection]="true" [value]="day.value">{{ day.long }}</bic-dropdown-element>
            }
          </bic-select>
        </div>
        <div class="flex flex-1 flex-col gap-1 min-w-0">
          <bic-select (valueChange)="setInterval($event)"
                      [label]="'Rhythmus'"
                      [value]="form.intervalWeeks">
            @for (interval of intervalOptions; track interval.value) {
              <bic-dropdown-element [value]="interval.value">{{ interval.label }}</bic-dropdown-element>
            }
          </bic-select>
        </div>
      </div>
    }

    <!-- Uhrzeit + Datum -->
    <div class="flex flex-row gap-4">
      @if (!expertMode) {
        <div class="flex flex-1 flex-col gap-1 min-w-0">
          <bic-input (valueChange)="setTime($event)"
                     [label]="'Uhrzeit'"
                     [placeholder]="'HH:MM, z. B. 08:30'"
                     [showX]="false"
                     [value]="form.timeOfDay">
          </bic-input>
        </div>
      }
      <div class="flex flex-1 flex-col gap-1 min-w-0">
        <bic-input (dateChange)="onStartDatePicked($event)"
                   (valueChange)="onStartDateText($event)"
                   [iconStart]="faCalendarDays"
                   [isDatepicker]="true"
                   [label]="form.pattern === 'ONCE' ? 'Datum' : 'Startdatum'"
                   [minDateToday]="true"
                   [ngModel]="startDateModel"
                   [placeholder]="'TT.MM.JJJJ'"
                   [showX]="false"
                   bicDatepickerInput>
        </bic-input>
      </div>
    </div>

    @if (form.pattern !== 'ONCE') {
      <div class="flex flex-row gap-4 items-end">
        <div class="flex flex-1 flex-col gap-1 min-w-0">
          <bic-input (dateChange)="onEndDatePicked($event)"
                     (valueChange)="onEndDateText($event)"
                     [iconStart]="faCalendarDays"
                     [isDatepicker]="true"
                     [label]="'Enddatum (optional)'"
                     [minDateToday]="true"
                     [ngModel]="endDateModel"
                     [placeholder]="'TT.MM.JJJJ'"
                     [showX]="false"
                     bicDatepickerInput>
          </bic-input>
        </div>
        <div class="flex flex-1 items-center h-[38px]">
          @if (form.endDate) {
            <button (click)="clearEndDate()" class="text-xs text-green hover:underline" type="button">
              Ohne Enddatum
            </button>
          }
        </div>
      </div>
    }

    <!-- Vorschau: Zeitzone + nächste Ausführungen -->
    <div class="flex items-start gap-2 px-3 py-2 bg-gray-100 rounded-lg">
      <fa-icon [icon]="faCircleInfo" class="text-gray-500 text-xs shrink-0 mt-1"></fa-icon>
      <span class="text-sm font-medium text-gray-500">
        Zeitzone: deutsche Zeit (Europe/Berlin).
        @switch (preview.kind) {
          @case ('loading') {
            <span class="inline-flex items-center gap-1">
              <fa-icon [icon]="faSpinnerThird" class="animate-spin text-xs"></fa-icon>
              Nächste Ausführungen werden berechnet…
            </span>
          }
          @case ('ready') {
            @if (nextRunsText) {
              Nächste Ausführungen: {{ nextRunsText }}
            } @else {
              Keine weitere Ausführung geplant.
            }
          }
        }
      </span>
    </div>

    @if (formIssues.length > 0 || previewIssues.length > 0) {
      <div class="flex flex-row items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
        <fa-icon [icon]="faTriangleExclamation" class="mt-0.5 shrink-0 text-sm text-yellow-700"></fa-icon>
        <span class="flex flex-col gap-1 text-xs text-yellow-900">
          @for (issue of formIssues; track $index) {
            <span>{{ issue }}</span>
          }
          @for (issue of previewIssues; track $index) {
            <span>{{ issue }}</span>
          }
        </span>
      </div>
    }
    @if (interactiveOption) {
      <!-- Ein Prüflauf mit Abfrage beim Start ist nicht planbar: direkt dorthin, wo die Auswahl festgelegt wird. -->
      <div class="flex flex-col gap-2 rounded-lg border border-gray-200 p-3">
        <span class="text-xs text-gray-700">
          {{ interactiveOption.startSelectionStep === 2
            ? 'Dieser Prüflauf fragt beim Start, welche Dateien geprüft werden.'
            : interactiveOption.startSelectionStep === 1
              ? 'Dieser Prüflauf fragt beim Start, welche Regeln geprüft werden.'
              : 'Dieser Prüflauf fragt beim Start nach Eingaben.' }}
          Für einen Zeitplan muss die Auswahl im Prüflauf fest hinterlegt sein.
        </span>
        @if (interactiveOption.editable) {
          <bic-button (onClick)="configureSelection()" [disabled]="saving || deleting" buttonType="white" class="w-fit">
            Auswahl im Prüflauf festlegen
          </bic-button>
        } @else {
          <span class="text-xs text-gray-500">
            Zentral bereitgestellte Prüfläufe lassen sich nicht ändern. Duplizieren Sie den Prüflauf in der
            Prüflauf-Übersicht und legen Sie die Auswahl in der Kopie fest.
          </span>
        }
      </div>
    }
    @if (preview.kind === 'error') {
      <div class="flex flex-row items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
        <fa-icon [icon]="faTriangleExclamation" class="mt-0.5 shrink-0 text-sm text-red"></fa-icon>
        <span class="text-xs text-red">{{ preview.message }}</span>
      </div>
    }

    <!-- Benachrichtigung -->
    <div class="flex flex-col rounded-lg border border-gray-200">
      <div class="flex items-center justify-between gap-4 p-4 border-b border-gray-200">
        <div class="flex items-center gap-3 min-w-0">
          <fa-icon [class]="form.notifyOnFailure ? 'text-gray-500' : 'text-gray-300'" [icon]="faTriangleExclamation"
                   class="text-lg shrink-0"></fa-icon>
          <div class="flex flex-col min-w-0">
            <span class="text-sm font-medium text-gray-900">E-Mail bei Fehlern</span>
            <span class="text-xs text-gray-500">Fehlgeschlagen, nicht gestartet, übersprungen oder pausiert</span>
          </div>
        </div>
        <bic-toggle (ngModelChange)="setNotifyOnFailure($event)" [ngModel]="form.notifyOnFailure"></bic-toggle>
      </div>
      <div [class.border-b]="notifyEnabled" [class.border-gray-200]="notifyEnabled"
           class="flex items-center justify-between gap-4 p-4">
        <div class="flex items-center gap-3 min-w-0">
          <fa-icon [class]="form.notifyOnSuccess ? 'text-gray-500' : 'text-gray-300'" [icon]="faCircleInfo"
                   class="text-lg shrink-0"></fa-icon>
          <div class="flex flex-col min-w-0">
            <span class="text-sm font-medium text-gray-900">E-Mail bei Erfolg</span>
            <span class="text-xs text-gray-500">Mit Link zum Bericht</span>
          </div>
        </div>
        <bic-toggle (ngModelChange)="setNotifyOnSuccess($event)" [ngModel]="form.notifyOnSuccess"></bic-toggle>
      </div>
      @if (notifyEnabled) {
        <div class="flex flex-col gap-4 p-4 main-sm:flex-row">
          <div class="flex flex-1 flex-col gap-1 min-w-0">
            @if (membersLoading) {
              <span class="text-sm font-medium text-gray-900">Projektmitglieder</span>
              <span class="flex items-center gap-2 text-xs text-gray-500 h-[38px]">
                <fa-icon [icon]="faSpinnerThird" class="animate-spin text-xs"></fa-icon>
                Mitglieder werden geladen…
              </span>
            } @else {
              <bic-select (valueChange)="setMembers($event)"
                          [label]="'Projektmitglieder'"
                          [multiple]="true"
                          [placeholder]="'Projektmitglieder auswählen'"
                          [value]="form.notifyUserIds">
                @for (member of memberOptions; track member.id) {
                  <bic-dropdown-element [selection]="true" [value]="member.id">{{ member.label }}</bic-dropdown-element>
                }
              </bic-select>
              @if (membersError) {
                <span class="text-xs text-red">Projektmitglieder konnten nicht geladen werden.</span>
              }
            }
          </div>
          <div class="flex flex-1 flex-col gap-1 min-w-0">
            <bic-input (valueChange)="setEmails($event)"
                       [label]="'Externe E-Mail-Adressen'"
                       [placeholder]="'musterfrau@musterunternehmen.de'"
                       [value]="form.notifyEmailsText">
            </bic-input>
            <span class="text-xs text-gray-500">Mehrere Adressen mit Komma trennen.</span>
          </div>
        </div>
      }
    </div>
    @if (removedRecipientsHint) {
      <div class="flex items-start gap-2 px-3 py-2 bg-gray-100 rounded-lg">
        <fa-icon [icon]="faCircleInfo" class="text-gray-500 text-xs shrink-0 mt-1"></fa-icon>
        <span class="text-sm font-medium text-gray-500">{{ removedRecipientsHint }}</span>
      </div>
    }

    <div class="flex flex-col gap-1 text-xs text-gray-500">
      <span>Der Prüflauf wird in Ihrem Namen ausgeführt und über das Kontingent dieses Projekts abgerechnet.</span>
      @if (schedule?.actingUserName) {
        <span>Bisher ausgeführt als: {{ schedule?.actingUserName }}.</span>
      }
    </div>

    @if (serverError) {
      <div class="flex flex-row items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
        <fa-icon [icon]="faTriangleExclamation" class="mt-0.5 shrink-0 text-sm text-red"></fa-icon>
        <span class="text-xs text-red">{{ serverError }}</span>
      </div>
    }
  </div>

  <div bicPopupButtonGroup class="flex flex-row w-full items-center gap-4">
    @if (isEdit) {
      <bic-button (onClick)="requestDelete()" [disabled]="saving" [loading]="deleting" buttonType="white">
        {{ confirmDelete ? 'Wirklich löschen?' : 'Zeitplan löschen' }}
      </bic-button>
    }
    <div class="flex-1"></div>
    <bic-button (onClick)="close()" [disabled]="saving || deleting" buttonType="white">Abbrechen</bic-button>
    <bic-button (onClick)="save()" [disabled]="!canSave" [loading]="saving" buttonType="primary">Speichern</bic-button>
  </div>
</bic-popup>
